/**
 * Cutscenes (ฉาก): the opening and the story's scenes, as data (docs/archive/OPENING.md, section 4).
 *
 * A scene is a timeline of cues. The World (scene.ts) runs its clock, locks the player out and keeps
 * them from harm while it plays, stops at a `wait` until the player answers, and applies its `end`
 * whether it was watched or skipped, so both leave the same state. Everything a cue shows (camera,
 * fades, panels, puppets, lines, sound) is drawn by the client as a function of (scene, t): the actors
 * are puppets, never entities. Pose, emote, sfx, fx and panel names are the client's; it maps the
 * ones it does not draw yet to a stand-in (see apps/client/src/render/scene.ts).
 *
 * Chapter one (บทที่หนึ่ง) is the opening (C0–C4), the elder's first lesson (the `three-forms` quest)
 * and the closing scene in the village (C5); see `CHAPTERS`.
 */
import { START, type ZoneId } from './zones';
import { PLAINS_SCENES } from './plainsOpening';

/**
 * A spot on the zone map: a landmark of the map, 'plaza', 'elder' (the elder's post), 'spawn', or
 * 'player' (where the player stood when the scene began), shifted by (dx, dy) tiles.
 */
export interface Spot {
  readonly at: string;
  readonly dx?: number;
  readonly dy?: number;
}

/**
 * Who appears. 'player' is drawn with the player's own look; others name a sprite set and, when they
 * stand in for something on the map (the elder, the pet, a townsperson), that is hidden meanwhile.
 */
export interface ActorDef {
  readonly sprite: string;
  /** `npc:<name>`: an NPC of the World by its name (the plains story's people, systems/plainsStory.ts). */
  readonly standsIn?: 'elder' | 'pet' | `folk:${string}` | `npc:${string}`;
  /** Panels only: drawn as an ink silhouette. */
  readonly ink?: boolean;
}

export type Focus = Spot | { readonly actor: string } | { readonly between: readonly [string, string] };

export type Cue =
  /** The camera pans to a focus over `dur` seconds (0 = cut) and settles at pixel scale `px`. */
  | {
      readonly at: number;
      readonly do: 'camera';
      readonly focus: Focus;
      readonly px?: number;
      readonly dur?: number;
    }
  /** The screen fades to a colour (or back to clear) over `dur`. */
  | {
      readonly at: number;
      readonly do: 'fade';
      readonly to: 'black' | 'white' | 'ink' | 'clear';
      readonly dur: number;
    }
  /** A full-screen ink panel (panel scenes), shown for `dur`. */
  | { readonly at: number; readonly do: 'panel'; readonly art: string; readonly dur: number }
  /** An actor stands at a spot (facing a direction in tiles). A 'player' actor moves the player too. */
  | {
      readonly at: number;
      readonly do: 'place';
      readonly actor: string;
      readonly spot: Spot;
      readonly face?: readonly [number, number];
    }
  /** An actor walks to a spot over `dur`. A 'player' actor is set down there when the walk ends. */
  | {
      readonly at: number;
      readonly do: 'walk';
      readonly actor: string;
      readonly to: Spot;
      readonly dur: number;
    }
  /** An actor takes a pose, an emote or a townsfolk act (by the client's name), until the next. */
  | { readonly at: number; readonly do: 'pose'; readonly actor: string; readonly pose: string }
  /** An emoji over an actor's head. */
  | { readonly at: number; readonly do: 'emoji'; readonly actor: string; readonly emoji: string }
  /** An effect on an actor or at a spot. */
  | {
      readonly at: number;
      readonly do: 'fx';
      readonly fx: string;
      readonly actor?: string;
      readonly spot?: Spot;
      readonly dur: number;
    }
  /** Words on the screen: narration, the stone's whisper, a date, a place name, the title. */
  | {
      readonly at: number;
      readonly do: 'caption';
      readonly text: string;
      readonly style: 'ink' | 'whisper' | 'date' | 'place' | 'title';
      readonly dur: number;
    }
  /** A dialogue box. `sceneAdvance` ends it early; otherwise it closes after `dur`. */
  | {
      readonly at: number;
      readonly do: 'say';
      readonly actor: string;
      readonly name: string;
      readonly text: string;
      readonly dur: number;
    }
  /** Stops the clock until the player answers (`sceneAdvance`). */
  | { readonly at: number; readonly do: 'wait'; readonly prompt: string }
  | { readonly at: number; readonly do: 'sfx'; readonly sfx: string }
  | { readonly at: number; readonly do: 'music'; readonly theme: string };

export type SceneTrigger =
  /** A new character's first entry into the world. */
  | { readonly on: 'newCharacter' }
  /** Walking within `r` tiles of a spot. */
  | { readonly on: 'near'; readonly spot: Spot; readonly r: number }
  /** Straight after another scene ends (or on load, if that one was seen and this one not). */
  | { readonly on: 'after'; readonly scene: string }
  /** Handing in a quest. */
  | { readonly on: 'questDone'; readonly quest: string };

export interface SceneDef {
  readonly id: string;
  /** panels: full-screen drawings, no map shown; stage: on the zone map. */
  readonly kind: 'panels' | 'stage';
  /** Stage scenes play only on this zone's map. */
  readonly zone?: ZoneId;
  /** Only with the 256 m plains (`plains256()`, data/zones.ts): origin A's opening (data/plainsOpening.ts). */
  readonly bigPlains?: boolean;
  readonly trigger: SceneTrigger;
  /** Only while the elder's quest chain is before this index. */
  readonly beforeQuest?: number;
  /** Only while the current quest is offered and not yet taken. */
  readonly offerOpen?: boolean;
  /** Seconds, not counting waits. */
  readonly length: number;
  readonly actors: Readonly<Record<string, ActorDef>>;
  /** In time order. */
  readonly cues: readonly Cue[];
  /** Applied when the scene ends, watched or skipped alike. */
  readonly end: {
    /** Where the player stands afterwards (else where the scene's own 'player' cues left them). */
    readonly placePlayer?: Spot;
    /** Open the elder's dialog as if the player had spoken to him (his current quest offer). */
    readonly talk?: 'elder';
    /** A "go here" marker the client shows until the player is within 3 tiles of it. */
    readonly guideTo?: 'elder' | Spot;
    /** A hint under the screen. */
    readonly notice?: string;
  };
}

const ELDER = 'ผู้เฒ่าหลิว';
const HAI = 'ลุงไห่';

/** C0 · ตำนานศิลาเก้ากระบี่: the legend in ink, played once for a new character. */
const legend: SceneDef = {
  id: 'opening-legend',
  kind: 'panels',
  trigger: { on: 'newCharacter' },
  length: 66,
  actors: {},
  cues: [
    { at: 0, do: 'fade', to: 'black', dur: 0 },
    { at: 0, do: 'panel', art: 'legend-ink', dur: 4 },
    { at: 0.3, do: 'fade', to: 'clear', dur: 1.5 },
    { at: 0.3, do: 'sfx', sfx: 'gong' },
    { at: 0.8, do: 'caption', text: 'สามร้อยปีก่อน', style: 'date', dur: 3 },
    { at: 4, do: 'panel', art: 'legend-waves', dur: 7 },
    { at: 4, do: 'sfx', sfx: 'waves' },
    { at: 4.5, do: 'caption', text: 'เด็กชายชาวประมงคนหนึ่ง ยืนฟันคลื่นอยู่ทั้งวัน', style: 'ink', dur: 3.2 },
    { at: 7.8, do: 'caption', text: 'คลื่นซัดเข้า คลื่นถอยกลับ คลื่นแตกฝั่ง', style: 'ink', dur: 3 },
    { at: 11, do: 'panel', art: 'legend-bandits', dur: 8 },
    { at: 11.5, do: 'caption', text: 'เขาชนะทุกคน ไม่มีใครยืนได้ถึงกระบวนท่าที่สาม', style: 'ink', dur: 3.5 },
    { at: 12.5, do: 'sfx', sfx: 'hit' },
    { at: 13.5, do: 'sfx', sfx: 'hit' },
    { at: 14.5, do: 'sfx', sfx: 'hit' },
    { at: 15.5, do: 'caption', text: 'จนวันหนึ่ง เขาจำไม่ได้ว่าคนที่ล้มลงทำผิดอะไร', style: 'ink', dur: 3.3 },
    { at: 19, do: 'panel', art: 'legend-swords', dur: 7 },
    { at: 19.5, do: 'caption', text: 'ยุทธภพเรียกเขาว่า เถาฉางเยี่ย', style: 'ink', dur: 3 },
    { at: 20, do: 'sfx', sfx: 'ching' },
    { at: 21.5, do: 'sfx', sfx: 'ching' },
    { at: 22.8, do: 'caption', text: 'ผู้ยืนลำพังตลอดคืนอันยาวนาน', style: 'ink', dur: 3 },
    { at: 23, do: 'sfx', sfx: 'ching' },
    { at: 26, do: 'panel', art: 'legend-stone', dur: 8 },
    { at: 26.5, do: 'caption', text: 'ในวัยชรา เขาปักกระบี่แปดเล่มแห่งชีวิตลงในศิลา', style: 'ink', dur: 7 },
    { at: 27, do: 'sfx', sfx: 'ching' },
    { at: 28.6, do: 'sfx', sfx: 'ching' },
    { at: 30, do: 'sfx', sfx: 'ching' },
    { at: 31.2, do: 'sfx', sfx: 'ching' },
    { at: 32.2, do: 'sfx', sfx: 'ching' },
    { at: 34, do: 'panel', art: 'legend-hand', dur: 5 },
    { at: 34.3, do: 'caption', text: 'ร่องที่เก้า เขาวางเพียงมือเปล่า', style: 'ink', dur: 2.3 },
    { at: 35, do: 'sfx', sfx: 'nine' },
    { at: 36.7, do: 'caption', text: 'ใจคือกระบี่ กระบี่คือใจ', style: 'ink', dur: 2.2 },
    { at: 39, do: 'panel', art: 'legend-under', dur: 7 },
    { at: 39.4, do: 'caption', text: 'แต่ยังมีกระบี่อีกเล่มที่เขาทิ้งไม่ได้', style: 'ink', dur: 3 },
    { at: 40, do: 'sfx', sfx: 'heartbeat' },
    {
      at: 42.5,
      do: 'caption',
      text: 'เขาผนึกมันไว้ใต้ศิลา ใช้กระบี่เก้าเล่มเป็นกุญแจ',
      style: 'ink',
      dur: 3.3,
    },
    { at: 43, do: 'sfx', sfx: 'heartbeat' },
    { at: 46, do: 'panel', art: 'legend-heirs', dur: 6 },
    {
      at: 46.3,
      do: 'caption',
      text: 'หลายชั่วคนต่อมา กระบี่ถูกนำกลับสู่แผ่นดินของมันทีละเล่ม',
      style: 'ink',
      dur: 3,
    },
    { at: 49.4, do: 'caption', text: 'ทุกเล่มที่ออกไป ผนึกก็อ่อนลง', style: 'ink', dur: 2.5 },
    { at: 52, do: 'panel', art: 'legend-thief', dur: 8 },
    { at: 52.3, do: 'caption', text: 'ยี่สิบปีก่อน', style: 'date', dur: 2.2 },
    { at: 54.5, do: 'caption', text: 'เจ้าจะไม่มีวันพ่ายแพ้', style: 'whisper', dur: 2.3 },
    { at: 54.5, do: 'sfx', sfx: 'whisper' },
    { at: 57, do: 'caption', text: 'คืนนั้น กระบี่เล่มที่สิบหายไปจากใต้ศิลา', style: 'ink', dur: 2.8 },
    { at: 58.5, do: 'sfx', sfx: 'boom' },
    { at: 60, do: 'panel', art: 'legend-title', dur: 6 },
    {
      at: 60.5,
      do: 'caption',
      text: 'ผู้ที่ไม่เคยแพ้ คือผู้ที่โดดเดี่ยวที่สุดในยุทธภพ',
      style: 'ink',
      dur: 4.2,
    },
    { at: 62, do: 'caption', text: 'เก้ากระบี่', style: 'title', dur: 3 },
    { at: 62, do: 'sfx', sfx: 'gong' },
    { at: 64.8, do: 'fade', to: 'black', dur: 1.2 },
  ],
  end: {},
};

/** C1 · สัญญาเลือด: the vision on the night ferry; the player places their palm on the ninth groove. */
const pact: SceneDef = {
  id: 'opening-pact',
  kind: 'panels',
  trigger: { on: 'after', scene: 'opening-legend' },
  length: 24,
  actors: { me: { sprite: 'player', ink: true } },
  cues: [
    { at: 0, do: 'fade', to: 'black', dur: 0 },
    { at: 0, do: 'panel', art: 'pact-boat', dur: 4 },
    { at: 0.2, do: 'fade', to: 'clear', dur: 1 },
    { at: 0.3, do: 'sfx', sfx: 'waves' },
    { at: 0.6, do: 'caption', text: 'ปีนี้', style: 'date', dur: 3 },
    { at: 4, do: 'panel', art: 'pact-stone', dur: 5 },
    { at: 4.4, do: 'caption', text: 'ศิลาเก้ากระบี่ ร้อนดุจเลือด', style: 'ink', dur: 4.2 },
    { at: 4.5, do: 'sfx', sfx: 'heartbeat' },
    { at: 9, do: 'panel', art: 'pact-near', dur: 5 },
    { at: 9.3, do: 'caption', text: 'ร่องที่เก้ายังว่าง', style: 'whisper', dur: 2.3 },
    { at: 9.3, do: 'sfx', sfx: 'whisper' },
    { at: 11.7, do: 'caption', text: 'เจ้าจะถือกระบี่ที่ไม่มีรูป ได้หรือไม่', style: 'whisper', dur: 2.3 },
    { at: 14, do: 'panel', art: 'pact-palm', dur: 4 },
    { at: 14, do: 'wait', prompt: 'คลิก หรือกด Space เพื่อวางฝ่ามือลงบนร่องที่เก้า' },
    { at: 14.2, do: 'sfx', sfx: 'hurt' },
    { at: 15.5, do: 'sfx', sfx: 'nine' },
    { at: 18, do: 'panel', art: 'pact-white', dur: 6 },
    { at: 18, do: 'sfx', sfx: 'charge' },
    { at: 18.6, do: 'caption', text: 'สัญญาเลือด', style: 'title', dur: 2.2 },
    { at: 19, do: 'sfx', sfx: 'boom' },
    { at: 21, do: 'caption', text: 'ผู้ลงนามสัญญาเลือด… จงตื่น', style: 'whisper', dur: 2.4 },
    { at: 23, do: 'fade', to: 'white', dur: 1 },
  ],
  end: {},
};

/** C2 · ตื่นที่ท่าเรือ: the player wakes on the pier; Uncle Hai sends them to the elder. */
const wake: SceneDef = {
  id: 'opening-wake',
  kind: 'stage',
  zone: START, // the opening zone (zones.ts), where the story's places are landmarks
  trigger: { on: 'after', scene: 'opening-pact' },
  length: 25,
  actors: {
    me: { sprite: 'player' },
    hai: { sprite: 'fisherOld', standsIn: 'folk:hai' },
    fox: { sprite: 'fox', standsIn: 'pet' },
  },
  cues: [
    { at: 0, do: 'fade', to: 'white', dur: 0 },
    { at: 0, do: 'place', actor: 'me', spot: { at: 'wake' }, face: [1, 0] },
    { at: 0, do: 'pose', actor: 'me', pose: 'sit' },
    { at: 0, do: 'place', actor: 'hai', spot: { at: 'pier' }, face: [0, -1] },
    { at: 0, do: 'pose', actor: 'hai', pose: 'fish' },
    { at: 0, do: 'place', actor: 'fox', spot: { at: 'wake', dx: 3, dy: 0.4 }, face: [-1, 0] },
    { at: 0, do: 'camera', focus: { actor: 'me' }, px: 5, dur: 0 },
    { at: 0, do: 'music', theme: 'none' },
    { at: 0.2, do: 'fade', to: 'clear', dur: 2 },
    { at: 0.5, do: 'sfx', sfx: 'waves' },
    { at: 1, do: 'walk', actor: 'fox', to: { at: 'wake', dx: 0.8, dy: 0.2 }, dur: 1.6 },
    { at: 3, do: 'pose', actor: 'hai', pose: 'glance' },
    { at: 3.3, do: 'emoji', actor: 'hai', emoji: 'shock' },
    { at: 3.5, do: 'say', actor: 'hai', name: HAI, text: 'อ้าว! นึกว่าศพลอยมาติดท่าเสียแล้ว', dur: 3.5 },
    { at: 7, do: 'camera', focus: { between: ['me', 'hai'] }, px: 4, dur: 2 },
    { at: 7.5, do: 'walk', actor: 'fox', to: { at: 'wake', dx: 1.2, dy: 1 }, dur: 1 },
    {
      at: 7.5,
      do: 'say',
      actor: 'hai',
      name: HAI,
      text: 'เรือข้ามฟากเมื่อคืนส่งเจ้าไว้ที่นี่ หลับเป็นตายเชียว',
      dur: 4.3,
    },
    { at: 8, do: 'pose', actor: 'me', pose: 'idle' },
    { at: 8, do: 'sfx', sfx: 'land' },
    { at: 12, do: 'camera', focus: { actor: 'me' }, px: 5, dur: 1.2 },
    { at: 12.2, do: 'pose', actor: 'me', pose: 'think' },
    { at: 12.6, do: 'fx', fx: 'palmGlow', actor: 'me', dur: 1.6 },
    { at: 12.6, do: 'sfx', sfx: 'heartbeat' },
    { at: 16.6, do: 'pose', actor: 'me', pose: 'idle' },
    { at: 17, do: 'camera', focus: { between: ['me', 'hai'] }, px: 4, dur: 1 },
    { at: 17, do: 'walk', actor: 'hai', to: { at: 'wake', dx: -1.6, dy: -0.4 }, dur: 1.4 },
    { at: 18.4, do: 'pose', actor: 'hai', pose: 'point' },
    {
      at: 18.4,
      do: 'say',
      actor: 'hai',
      name: HAI,
      text: 'มือเจ้าเลือดออกนี่ ไปหาผู้เฒ่าหลิวที่ลานกลางหมู่บ้านเถอะ',
      dur: 2.8,
    },
    { at: 21.2, do: 'camera', focus: { at: 'shrine', dx: 4 }, dur: 1.5 },
    {
      at: 21.2,
      do: 'say',
      actor: 'hai',
      name: HAI,
      text: 'ตาเฒ่านั่นถามหาคนแปลกหน้ามาตั้งแต่ไก่โห่ แปลกคนจริงๆ',
      dur: 3.6,
    },
    { at: 21.5, do: 'walk', actor: 'hai', to: { at: 'pier' }, dur: 2 },
    { at: 21.5, do: 'music', theme: 'town' },
    { at: 23.5, do: 'pose', actor: 'hai', pose: 'fish' },
    { at: 23.5, do: 'camera', focus: { actor: 'me' }, dur: 1.4 },
  ],
  end: {
    placePlayer: { at: 'wake' },
    guideTo: 'elder',
    notice: 'คลิกที่พื้นเพื่อเดิน · ไปหาผู้เฒ่าหลิวที่ลานกลางหมู่บ้าน',
  },
};

/** C3 · เสียงกระบี่ไผ่: passing the fishermen's shrine, the palm answers the bamboo sword. */
const bambooHum: SceneDef = {
  id: 'bamboo-hum',
  kind: 'stage',
  zone: START, // the opening zone (zones.ts), where the story's places are landmarks
  // The courtyard opens onto the village road: anyone walking down it passes within this.
  trigger: { on: 'near', spot: { at: 'shrineRoad' }, r: 9 },
  beforeQuest: 1,
  length: 12,
  actors: { me: { sprite: 'player' } },
  cues: [
    { at: 0, do: 'pose', actor: 'me', pose: 'idle' },
    { at: 0, do: 'fx', fx: 'palmGlow', actor: 'me', dur: 3 },
    { at: 0, do: 'sfx', sfx: 'ching' },
    // px 1: far enough out (~34 m) to see the shrine (16 x 20 m, 9 m eave) from outside, not from within its floor.
    { at: 3, do: 'camera', focus: { at: 'shrineTop' }, px: 1, dur: 1.5 },
    { at: 3.5, do: 'fx', fx: 'shrineHum', spot: { at: 'shrineTop' }, dur: 4.5 },
    { at: 4, do: 'caption', text: 'ศาลเจ้าชาวประมง', style: 'place', dur: 3 },
    { at: 5, do: 'sfx', sfx: 'nine' },
    { at: 8, do: 'camera', focus: { actor: 'me' }, dur: 1.5 },
    { at: 9, do: 'pose', actor: 'me', pose: 'glance' },
    { at: 9.5, do: 'emoji', actor: 'me', emoji: 'question' },
    { at: 11, do: 'pose', actor: 'me', pose: 'idle' },
  ],
  end: { guideTo: 'elder' },
};

/** C4 · ผู้เฝ้าศิลา: the elder sees the palm, tells of the stone and the stolen sword, and offers to teach. */
const elderMeets: SceneDef = {
  id: 'elder-meets',
  kind: 'stage',
  zone: START, // the opening zone (zones.ts), where the story's places are landmarks
  trigger: { on: 'near', spot: { at: 'elder' }, r: 3 },
  beforeQuest: 1,
  offerOpen: true,
  length: 45,
  actors: {
    me: { sprite: 'player' },
    elder: { sprite: 'elder', standsIn: 'elder' },
  },
  cues: [
    { at: 0, do: 'place', actor: 'elder', spot: { at: 'elder' }, face: [-1, 0] },
    { at: 0, do: 'pose', actor: 'elder', pose: 'fold' },
    { at: 0, do: 'camera', focus: { between: ['me', 'elder'] }, px: 4, dur: 1 },
    { at: 0, do: 'walk', actor: 'me', to: { at: 'elder', dx: -1.5, dy: 0.3 }, dur: 1.1 },
    { at: 1.2, do: 'pose', actor: 'me', pose: 'salute' },
    { at: 3, do: 'camera', focus: { actor: 'me' }, px: 5, dur: 0.8 },
    { at: 3, do: 'pose', actor: 'elder', pose: 'glance' },
    { at: 3.2, do: 'pose', actor: 'me', pose: 'think' },
    { at: 3.2, do: 'fx', fx: 'palmGlow', actor: 'me', dur: 3 },
    { at: 3.4, do: 'say', actor: 'elder', name: ELDER, text: 'ฝ่ามือนั้น… ศิลาเรียกเจ้าด้วยหรือ', dur: 3.4 },
    { at: 7, do: 'camera', focus: { between: ['me', 'elder'] }, px: 4, dur: 0.8 },
    { at: 7, do: 'pose', actor: 'me', pose: 'idle' },
    { at: 7, do: 'pose', actor: 'elder', pose: 'think' },
    {
      at: 7.2,
      do: 'say',
      actor: 'elder',
      name: ELDER,
      text: 'เมื่อคืน ศิลาเก้ากระบี่ร้อนดุจเลือด เป็นครั้งแรกในสามร้อยปี',
      dur: 4.6,
    },
    { at: 12, do: 'pose', actor: 'elder', pose: 'idle' },
    {
      at: 12.1,
      do: 'say',
      actor: 'elder',
      name: ELDER,
      text: 'เจ้าไม่ใช่คนแรกที่มาพร้อมแผลเช่นนี้ และคงไม่ใช่คนสุดท้าย',
      dur: 4.7,
    },
    { at: 17, do: 'camera', focus: { actor: 'elder' }, px: 5, dur: 1 },
    { at: 17, do: 'pose', actor: 'elder', pose: 'fold' },
    {
      at: 17.2,
      do: 'say',
      actor: 'elder',
      name: ELDER,
      text: 'ใต้ศิลาเคยมีกระบี่เล่มที่สิบถูกผนึกไว้ ยี่สิบปีก่อน มันถูกขุดออกไป',
      dur: 5.6,
    },
    { at: 23, do: 'emoji', actor: 'me', emoji: 'question' },
    { at: 23, do: 'pose', actor: 'elder', pose: 'think' },
    { at: 24, do: 'say', actor: 'elder', name: ELDER, text: 'ผู้ใดหรือ… เรื่องนั้นไว้ก่อนเถิด', dur: 3 },
    { at: 27, do: 'camera', focus: { at: 'grove' }, px: 3, dur: 1.6 },
    { at: 27, do: 'pose', actor: 'elder', pose: 'point' },
    {
      at: 27.2,
      do: 'say',
      actor: 'elder',
      name: ELDER,
      text: 'นับแต่นั้นปราณมารซึมไปทั่ว หมาป่าบนเขาตะวันออกหอนทุกคืน',
      dur: 5.6,
    },
    { at: 31, do: 'camera', focus: { between: ['me', 'elder'] }, px: 4, dur: 1.4 },
    { at: 33, do: 'pose', actor: 'elder', pose: 'fold' },
    {
      at: 33.1,
      do: 'say',
      actor: 'elder',
      name: ELDER,
      text: 'สัญญาเลือดผูกเจ้าไว้กับร่องที่เก้า กระบี่ที่ไม่มีรูป',
      dur: 2.9,
    },
    {
      at: 36,
      do: 'say',
      actor: 'elder',
      name: ELDER,
      text: 'แต่กระบี่ร่องว่างต้องเริ่มจากมือที่อ่านจังหวะคนออก',
      dur: 3,
    },
    { at: 39, do: 'camera', focus: { between: ['me', 'elder'] }, px: 5, dur: 1 },
    { at: 39, do: 'pose', actor: 'elder', pose: 'bow' },
    {
      at: 39.1,
      do: 'say',
      actor: 'elder',
      name: ELDER,
      text: 'ข้าจะสอนเจ้าเอง เริ่มจากสามกระบวนท่า',
      dur: 2.8,
    },
    { at: 40, do: 'pose', actor: 'elder', pose: 'idle' },
    { at: 40.5, do: 'pose', actor: 'me', pose: 'salute' },
    {
      at: 42,
      do: 'say',
      actor: 'elder',
      name: ELDER,
      text: 'ที่เถาฉางเยี่ยเรียนจากคลื่นทะเลหน้าหมู่บ้านนี้',
      dur: 3,
    },
  ],
  end: { placePlayer: { at: 'elder', dx: -1.5, dy: 0.3 }, talk: 'elder' },
};

/**
 * C5 · คลื่นหน้าหมู่บ้าน (new, closing chapter one): the first lesson handed in, the elder sends the
 * player on toward the stone; the next quest is offered.
 */
const chapterOneEnd: SceneDef = {
  id: 'chapter-one-end',
  kind: 'stage',
  zone: START, // the opening zone (zones.ts), where the story's places are landmarks
  trigger: { on: 'questDone', quest: 'three-forms' },
  length: 20,
  actors: {
    me: { sprite: 'player' },
    elder: { sprite: 'elder', standsIn: 'elder' },
  },
  cues: [
    { at: 0, do: 'place', actor: 'elder', spot: { at: 'elder' }, face: [-1, 0] },
    { at: 0, do: 'pose', actor: 'elder', pose: 'fold' },
    { at: 0, do: 'camera', focus: { between: ['me', 'elder'] }, px: 4, dur: 1 },
    {
      at: 0.5,
      do: 'say',
      actor: 'elder',
      name: ELDER,
      text: 'คลื่นซัดเข้า คลื่นถอยกลับ คลื่นแตกฝั่ง เจ้าอ่านมันออกแล้ว',
      dur: 3.8,
    },
    { at: 4.5, do: 'camera', focus: { at: 'pier', dy: 6 }, px: 3, dur: 2 },
    { at: 4.5, do: 'pose', actor: 'elder', pose: 'point' },
    { at: 4.5, do: 'sfx', sfx: 'waves' },
    {
      at: 4.7,
      do: 'say',
      actor: 'elder',
      name: ELDER,
      text: 'สามร้อยปีก่อน เด็กคนหนึ่งเรียนสิ่งเดียวกันจากทะเลหน้าหมู่บ้านนี้ ด้วยกระบี่ไม้ไผ่',
      dur: 4.6,
    },
    { at: 9.5, do: 'camera', focus: { between: ['me', 'elder'] }, px: 4, dur: 1.5 },
    { at: 9.5, do: 'pose', actor: 'elder', pose: 'think' },
    {
      at: 9.7,
      do: 'say',
      actor: 'elder',
      name: ELDER,
      text: 'ศิลาอยู่ที่ลานศิลาเก้ากระบี่ ทางใต้สุดถนน โจรภูเขากำลังรังควานผู้เดินทาง',
      dur: 4.4,
    },
    { at: 14.2, do: 'pose', actor: 'elder', pose: 'fold' },
    { at: 14.2, do: 'pose', actor: 'me', pose: 'salute' },
    { at: 14.3, do: 'caption', text: 'จบบทที่หนึ่ง · คนแปลกหน้าแห่งหยานหยู', style: 'title', dur: 4 },
    { at: 14.3, do: 'sfx', sfx: 'gong' },
    { at: 18.5, do: 'pose', actor: 'me', pose: 'idle' },
  ],
  end: { talk: 'elder' },
};

/**
 * C6 · สามประตูหยานหยู (closing chapter two): the black sword's thief laid to rest, the elder turns
 * south to the stone. The village gives the player no more lessons; the road through the village gate
 * leads on to ลานศิลาเก้ากระบี่. Directions are by compass and landmark (docs/archive/YANYU.md).
 */
const chapterTwoEnd: SceneDef = {
  id: 'chapter-two-end',
  kind: 'stage',
  zone: START, // the opening zone (zones.ts), where the story's places are landmarks
  trigger: { on: 'questDone', quest: 'black-sword' },
  length: 22,
  actors: {
    me: { sprite: 'player' },
    elder: { sprite: 'elder', standsIn: 'elder' },
  },
  cues: [
    { at: 0, do: 'place', actor: 'elder', spot: { at: 'elder' }, face: [0, 1] },
    { at: 0, do: 'pose', actor: 'elder', pose: 'think' },
    { at: 0, do: 'camera', focus: { between: ['me', 'elder'] }, px: 4, dur: 1 },
    {
      at: 0.5,
      do: 'say',
      actor: 'elder',
      name: ELDER,
      text: 'ข้าสอนเขาเองกับมือ ทุกกระบวนท่าที่เขาใช้กับเจ้า ข้าเป็นคนสอน',
      dur: 4,
    },
    { at: 4.6, do: 'pose', actor: 'elder', pose: 'fold' },
    {
      at: 4.8,
      do: 'say',
      actor: 'elder',
      name: ELDER,
      text: 'แต่กระบี่สีดำไม่ได้อยู่ที่ลานเลือด มันไปอยู่ในมือผู้ใด หรือมันไปเอง ข้าก็ไม่รู้',
      dur: 4.4,
    },
    { at: 9.4, do: 'camera', focus: { at: 'gate' }, px: 3, dur: 2 },
    { at: 9.4, do: 'pose', actor: 'elder', pose: 'point' },
    {
      at: 9.6,
      do: 'say',
      actor: 'elder',
      name: ELDER,
      text: 'ถนนทางใต้ลอดหอกลอง ผ่านซุ้มประตูหมู่บ้าน ไปสุดทางที่ลานศิลาเก้ากระบี่',
      dur: 4.4,
    },
    { at: 14.2, do: 'camera', focus: { between: ['me', 'elder'] }, px: 4, dur: 1.5 },
    { at: 14.2, do: 'pose', actor: 'elder', pose: 'idle' },
    {
      at: 14.4,
      do: 'say',
      actor: 'elder',
      name: ELDER,
      text: 'หยานหยูสอนเจ้าได้เท่านี้ ที่เหลือ ยุทธภพจะสอนเอง',
      dur: 3.4,
    },
    { at: 17.9, do: 'pose', actor: 'me', pose: 'salute' },
    { at: 18, do: 'caption', text: 'จบบทที่สอง · สามประตูหยานหยู', style: 'title', dur: 3.8 },
    { at: 18, do: 'sfx', sfx: 'gong' },
    { at: 21, do: 'pose', actor: 'me', pose: 'idle' },
  ],
  end: {
    talk: 'elder',
    guideTo: { at: 'gate' },
    notice: 'ถนนทางใต้ผ่านซุ้มประตูหมู่บ้าน ไปลานศิลาเก้ากระบี่',
  },
};

export const SCENE_LIST: readonly SceneDef[] = [
  legend,
  pact,
  wake,
  bambooHum,
  elderMeets,
  chapterOneEnd,
  chapterTwoEnd,
];

/**
 * Every scene the runner (scene.ts) looks through, in order: the story's, then the 256 m plains' opening and first
 * hour (data/plainsOpening.ts, `bigPlains`: only with that map on; drawn by apps/client0).
 */
export const ALL_SCENES: readonly SceneDef[] = [...SCENE_LIST, ...PLAINS_SCENES];
export const SCENES: Readonly<Record<string, SceneDef>> = Object.fromEntries(
  ALL_SCENES.map((s) => [s.id, s]),
);

/** The opening: everything a character from before cutscenes existed is counted as having seen. */
export const OPENING_SCENES: readonly string[] = [
  'opening-legend',
  'opening-pact',
  'opening-wake',
  'bamboo-hum',
  'elder-meets',
];

/** Scenes that can be watched again from the help window (only once seen). */
export const REPLAYABLE_SCENES: readonly string[] = ['opening-legend', 'opening-pact'];

/**
 * The story's chapters. A chapter is done once its quest is handed in; its legend chapter (data/lore)
 * opens then, and its closing scene plays where it ends.
 */
export interface ChapterDef {
  readonly n: number;
  readonly title: string;
  readonly zone: ZoneId;
  /** The scenes in it, in order. */
  readonly scenes: readonly string[];
  /** The quest whose hand-in ends it. */
  readonly quest: string;
  /** The legend chapter it opens (a title in data/lore LEGEND). */
  readonly legend: string;
}

export const CHAPTERS: readonly ChapterDef[] = [
  {
    n: 1,
    title: 'บทที่หนึ่ง · คนแปลกหน้าแห่งหยานหยู',
    zone: START, // the opening zone (zones.ts), where the story's places are landmarks
    scenes: [...OPENING_SCENES, 'chapter-one-end'],
    quest: 'three-forms',
    legend: 'เล่มที่หนึ่ง · กระบี่ไผ่',
  },
  {
    n: 2,
    title: 'บทที่สอง · สามประตูหยานหยู',
    zone: START, // the opening zone (zones.ts), where the story's places are landmarks
    scenes: ['chapter-two-end'],
    quest: 'black-sword',
    legend: 'บทแทรก · ศิษย์ผู้เฝ้าศิลา',
  },
];
